import type { CSSProperties, ReactElement, ReactNode } from "react";
import { Link } from "react-router-dom";
import { color, radius, space } from "@tokens/index";
import { Surface, Text, useViewport } from "@primitives/index";
import { EnvironmentMarker } from "../EnvironmentMarker";
import {
  APP_NAME,
  SEARCH_MAX_WIDTH,
  TOP_BAR_HEIGHT,
  TOP_BAR_HEIGHT_COMPACT,
  TOP_BAR_SIDE_MIN_WIDTH,
  contentGutter,
  searchMinWidth,
} from "./shellMeasures";

/**
 * TopBar: the one shell chrome.
 *
 * Sticky and opaque (`chromeSurface`). No rule under it: the tonal step
 * alone separates it from the canvas. 52px tall (48px on
 * `compact`). Slots:
 *
 *  - `leading`      a back action or nothing.
 *  - brand          the typographic wordmark plus the environment chip. It
 *                   links home and truncates before anything else does.
 *  - `contextTitle` a truncating title after the brand.
 *  - `search`       on `expanded`+ the bar is a three-column grid with equal
 *                   side floors, so the well sits on the optical center and
 *                   caps at `SEARCH_MAX_WIDTH`. On `medium` the well fills
 *                   the middle. Callers omit it on `compact` and put a search
 *                   trigger in `actions` instead.
 *  - `actions`      trailing controls. Callers decide the per-class set.
 *
 * Nothing in the bar wraps. Every text cell has `minWidth: 0` and
 * truncates.
 */

export interface TopBarProps {
  readonly leading?: ReactNode;
  readonly contextTitle?: string;
  readonly search?: ReactNode;
  readonly actions: ReactNode;
}

const clusterStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  minWidth: 0,
};

const brandLinkStyle: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: space[2],
  minHeight: 36,
  paddingInline: space[1],
  borderRadius: radius.md,
  textDecoration: "none",
  color: "inherit",
  minWidth: 0,
};

const dividerStyle: CSSProperties = {
  width: 1,
  height: 18,
  background: color.border.subtle,
  flexShrink: 0,
};

const searchWellStyle: CSSProperties = {
  display: "flex",
  justifyContent: "center",
  minWidth: 0,
};

export function TopBar({ leading, contextTitle, search, actions }: TopBarProps): ReactElement {
  const { viewport, isCompact, isAtLeast } = useViewport();
  const hasSearch = search !== undefined && search !== null;
  const centered = hasSearch && isAtLeast("expanded");
  const gap = isCompact ? space[2] : space[3];

  const headerStyle: CSSProperties = {
    position: "sticky",
    top: 0,
    zIndex: 20,
    display: "grid",
    gridTemplateColumns: centered
      ? `minmax(${String(TOP_BAR_SIDE_MIN_WIDTH)}px, 1fr) minmax(${String(searchMinWidth(viewport))}px, ${String(SEARCH_MAX_WIDTH)}px) minmax(${String(TOP_BAR_SIDE_MIN_WIDTH)}px, 1fr)`
      : hasSearch
        ? `auto minmax(${String(searchMinWidth(viewport))}px, 1fr) auto`
        : "minmax(0, 1fr) auto",
    alignItems: "center",
    gap,
    height: isCompact ? TOP_BAR_HEIGHT_COMPACT : TOP_BAR_HEIGHT,
    paddingInline: contentGutter(viewport),
    border: "none",
    borderRadius: 0,
    flexShrink: 0,
  };

  return (
    <Surface as="header" material="chrome" style={headerStyle}>
      <div style={{ ...clusterStyle, gap }}>
        {leading}
        <Link to="/workflows" aria-label={`${APP_NAME} — go to home`} style={brandLinkStyle}>
          <Text role="brand" size={isCompact ? "base" : "lg"} tracking="caps-loose" truncate>
            {APP_NAME}
          </Text>
          <EnvironmentMarker />
        </Link>
        {contextTitle !== undefined && contextTitle.length > 0 && (
          <>
            <span aria-hidden="true" style={dividerStyle} />
            <Text role="body" weight="medium" tone="muted" truncate style={{ minWidth: 0 }}>
              {contextTitle}
            </Text>
          </>
        )}
      </div>

      {hasSearch && (
        <div style={searchWellStyle}>
          <div style={{ width: "100%", minWidth: 0 }}>{search}</div>
        </div>
      )}

      <div
        style={{
          ...clusterStyle,
          gap: isCompact ? space[1] : space[2],
          justifyContent: "flex-end",
          flexShrink: 0,
        }}
      >
        {actions}
      </div>
    </Surface>
  );
}
